import { useEffect, useState } from 'react'
import { Zap, Activity, Eraser, Unlock, Package, RefreshCw, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { fmtDate } from '../i18n/format'
import UpdatePackagesModal from './UpdatePackagesModal'

const okValue = (v) => v === 'active' || v === 'connected' || v === true

// Botão de comando: definido no MÓDULO (não dentro do render) para não remontar
// a cada re-render do pai — se fosse recriado por render, o React trocaria o
// elemento no DOM a cada evento WS, fazendo o hover piscar e derrubando o clique
// (o botão sumia entre mousedown/mouseup, impossível de clicar no stress test).
function CmdBtn({ icon: Icon, label, onClick, isRunning, disabled, cls, title }) {
  return (
    <button onClick={onClick} disabled={disabled} title={title}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${cls}`}>
      {isRunning ? <Loader2 size={12} className="animate-spin" /> : <Icon size={12} />} {label}
    </button>
  )
}

// Comandos do device (Ping/Health) + caixa de output dedicada. Tema claro (drawer) ou escuro (mapa).
// canUpdate default false: o botão de pacotes só habilita quando o perfil IoT do device
// declara a capability (antes o mapa não passava a prop e ele ficava ligado para todos).
export default function DeviceCommands({ deviceName, theme = 'light', canUpdate = false }) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const [running, setRunning] = useState(null) // 'ping' | 'health' | null
  const [output, setOutput] = useState(null)
  const [updateOpen, setUpdateOpen] = useState(false)

  // Trocar de device limpa a saída: o health do caminhão A ficava na tela sob o
  // cabeçalho do B.
  useEffect(() => { setOutput(null); setRunning(null); setUpdateOpen(false) }, [deviceName])

  const dark = theme === 'dark'
  const btn = dark
    ? 'bg-white/10 text-slate-200 hover:bg-brand hover:text-white'
    : 'bg-slate-100 text-slate-600 hover:bg-brand hover:text-white dark:bg-slate-800 dark:text-slate-300'
  const box = dark ? 'border-white/10 bg-black/20' : 'border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-900'
  const muted = dark ? 'text-slate-400' : 'text-slate-500 dark:text-slate-400'
  const val = dark ? 'text-slate-200' : 'text-slate-700 dark:text-slate-200'

  async function runPing() {
    setRunning('ping'); setOutput(null)
    try {
      const res = await fetch(`/api/devices/${encodeURIComponent(deviceName)}/ping`, { method: 'POST' })
      const data = await res.json()
      setOutput({
        cmd: 'ping', ok: !!data.ok, latencyMs: data.latencyMs,
        result: data.data?.result, ts: data.data?.vmc?.timestamp,
        error: data.ok ? null : (data.error || t('ops:devices.commands.no_response')),
      })
    } catch (e) { setOutput({ cmd: 'ping', ok: false, error: e.message }) }
    finally { setRunning(null) }
  }

  async function runHealth() {
    setRunning('health'); setOutput(null)
    try {
      const res = await fetch(`/api/devices/${encodeURIComponent(deviceName)}/health`, { method: 'POST' })
      const data = await res.json()
      const r = data.ok && data.data ? data.data.result : null
      setOutput({
        cmd: 'health', ok: !!r && r.status === 'ok',
        status: r?.status, services: r?.services, netbird: r?.netbird,
        error: r ? null : (data.error || t('ops:devices.commands.no_response')),
      })
    } catch (e) { setOutput({ cmd: 'health', ok: false, error: e.message }) }
    finally { setRunning(null) }
  }

  async function runCleanCache() {
    setRunning('clean'); setOutput(null)
    try {
      const res = await fetch(`/api/devices/${encodeURIComponent(deviceName)}/clean-user-cache`, { method: 'POST' })
      const data = await res.json()
      const ok = res.ok && !!data.ok && data.published !== false
      setOutput({ cmd: 'clean', ok, error: ok ? null : (data.error || t('ops:devices.commands.no_response')) })
    } catch (e) { setOutput({ cmd: 'clean', ok: false, error: e.message }) }
    finally { setRunning(null) }
  }

  // Destrava o checklist do VMC quando ele parou num item bloqueante (impedimento).
  // O backend agora responde 502 quando o comando não é publicado e `published:true`
  // quando é, então o verde só aparece com a publicação confirmada. O ramo "enviado,
  // sem confirmação" cobre um servidor antigo, que respondia ok:true incondicional: é
  // um override de bloqueio de pré-operação e o operador precisa saber que talvez não
  // tenha chegado.
  async function runOverride() {
    setRunning('override'); setOutput(null)
    try {
      const res = await fetch(`/api/devices/${encodeURIComponent(deviceName)}/checklist-override`, { method: 'POST' })
      const data = await res.json()
      const accepted = res.ok && !!data.ok && data.published !== false
      setOutput({
        cmd: 'override',
        ok: accepted,
        unconfirmed: accepted && data.published === undefined,
        error: accepted ? null : (data.error || t('ops:devices.commands.send_failed')),
      })
    } catch (e) { setOutput({ cmd: 'override', ok: false, error: e.message }) }
    finally { setRunning(null) }
  }

  // Remonta o pacote de assets (fotos do cadastro e do checklist) e manda a nova
  // referência ao VMC, que baixa e reinstala sem precisar de novo bootstrap.
  async function runAssetPack() {
    setRunning('assetpack'); setOutput(null)
    try {
      const res = await fetch(`/api/devices/${encodeURIComponent(deviceName)}/asset-pack`, { method: 'POST' })
      const data = await res.json()
      setOutput({
        cmd: 'assetpack', ok: !!data.ok,
        version: data.asset_pack?.version, checklistVersion: data.checklist_version,
        error: data.ok ? null : (data.error || t('ops:devices.commands.send_failed')),
      })
    } catch (e) { setOutput({ cmd: 'assetpack', ok: false, error: e.message }) }
    finally { setRunning(null) }
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <CmdBtn icon={Zap} label={t('ops:devices.commands.ping')} onClick={runPing} isRunning={running === 'ping'} disabled={!!running} cls={btn} />
        <CmdBtn icon={Activity} label={t('ops:devices.commands.health')} onClick={runHealth} isRunning={running === 'health'} disabled={!!running} cls={btn} />
        <CmdBtn icon={Eraser} label={t('ops:devices.commands.clean_cache')} onClick={runCleanCache} isRunning={running === 'clean'} disabled={!!running} cls={btn} />
        <CmdBtn icon={Unlock} label={t('ops:devices.commands.unlock_checklist')} onClick={runOverride} isRunning={running === 'override'} disabled={!!running} cls={btn} />
        <CmdBtn icon={Package} label={t('ops:devices.commands.asset_pack')} onClick={runAssetPack} isRunning={running === 'assetpack'} disabled={!!running} cls={btn} />
        <CmdBtn icon={RefreshCw} label={t('ops:devices.update.button')} onClick={() => setUpdateOpen(true)}
          isRunning={false} disabled={!!running || !canUpdate} cls={btn}
          title={canUpdate ? undefined : t('ops:devices.update.unsupported')} />
      </div>

      {updateOpen && canUpdate && can('developer') && <UpdatePackagesModal deviceName={deviceName} onClose={() => setUpdateOpen(false)} />}

      <div className={`rounded-lg border p-3 text-xs max-h-40 overflow-y-auto scrollbar-thin ${box}`}>
        {running && <p className={`flex items-center gap-1.5 ${muted}`}><Loader2 size={12} className="animate-spin" /> {t('ops:devices.commands.running', { cmd: running })}</p>}
        {!running && !output && <p className={muted}>{t('ops:devices.commands.none_yet')}</p>}

        {!running && output?.cmd === 'ping' && (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 font-mono">
            <span className={`uppercase ${muted}`}>ping</span>
            {output.ok ? (
              <>
                <span className="text-status-op">{output.result || 'pong'}</span>
                {output.latencyMs != null && <span className={val}>· {output.latencyMs} ms</span>}
                {output.ts && <span className={muted}>· {fmtDate(output.ts, { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>}
              </>
            ) : <span className="text-status-down">· {output.error}</span>}
          </div>
        )}

        {!running && output?.cmd === 'health' && (
          output.services ? (
            <div className="space-y-1">
              <div className="flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${output.status === 'ok' ? 'bg-status-op' : 'bg-status-stop'}`} />
                <span className={`font-medium ${val}`}>{output.status === 'ok' ? t('ops:devices.commands.healthy') : t('ops:devices.commands.degraded')}</span>
              </div>
              {Object.entries(output.services).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between">
                  <span className={muted}>{k}</span>
                  <span className={`font-mono flex items-center gap-1.5 ${val}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${okValue(v) ? 'bg-status-op' : 'bg-status-down'}`} />
                    {String(v)}
                  </span>
                </div>
              ))}
              {output.netbird?.ip && (
                <div className={`flex items-center justify-between pt-1 mt-1 border-t ${dark ? 'border-white/10' : 'border-slate-200 dark:border-slate-700'}`}>
                  <span className={muted}>netbird</span>
                  <span className={`font-mono ${val}`}>{output.netbird.ip}</span>
                </div>
              )}
            </div>
          ) : <span className="text-status-down">{output.error}</span>
        )}

        {!running && output?.cmd === 'clean' && (
          <div className="flex flex-wrap items-center gap-x-2 font-mono">
            <span className={`uppercase ${muted}`}>cache</span>
            {output.ok
              ? <span className="text-status-op">{t('ops:devices.commands.clean_sent')}</span>
              : <span className="text-status-down">· {output.error}</span>}
          </div>
        )}

        {!running && output?.cmd === 'override' && (
          <div className="flex flex-wrap items-center gap-x-2 font-mono">
            <span className={`uppercase ${muted}`}>checklist</span>
            {output.ok
              ? <span className={output.unconfirmed ? 'text-status-stop' : 'text-status-op'}>
                  {output.unconfirmed ? t('ops:devices.commands.override_unconfirmed') : t('ops:devices.commands.override_sent')}
                </span>
              : <span className="text-status-down">· {output.error}</span>}
          </div>
        )}

        {!running && output?.cmd === 'assetpack' && (
          <div className="flex flex-wrap items-center gap-x-2 font-mono">
            <span className={`uppercase ${muted}`}>assets</span>
            {output.ok ? (
              <>
                <span className="text-status-op">{t('ops:devices.commands.asset_pack_sent')}</span>
                {output.version != null && <span className={val}>· pack v{output.version}</span>}
                {output.checklistVersion != null && <span className={muted}>· checklist v{output.checklistVersion}</span>}
              </>
            ) : <span className="text-status-down">· {output.error}</span>}
          </div>
        )}
      </div>
    </div>
  )
}
